/**
 * Lunch Expense List Page - View expenses (for all employees)
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { lunchExpenseApi } from '../api/lunchExpenseApi'
import { useAuth } from '@/core/auth/AuthContext'
import { Link } from 'react-router-dom'
import LoadingPage from '@/shared/components/LoadingPage'
import type { MonthlyBreakdown } from '../types/lunchExpense.types'

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

// ─── Monthly bar chart ────────────────────────────────────────────────────────

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function MonthlyBarChart({
  breakdown,
  selectedMonth,
  onSelectMonth,
  formatCurrency,
}: {
  breakdown: MonthlyBreakdown[]
  selectedMonth: number
  onSelectMonth: (month: number) => void
  formatCurrency: (n: number) => string
}) {
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null)

  // Ensure all 12 months represented
  const months = Array.from({ length: 12 }, (_, i) => {
    const found = breakdown.find((d) => d.month === i + 1)
    return found ?? { month: i + 1, month_name: MONTH_ABBRS[i], total_amount: 0, entry_count: 0 }
  })

  const maxAmount = Math.max(...months.map((m) => m.total_amount), 1)

  // Layout constants
  const W = 780
  const H = 210
  const PL = 0   // no Y-axis labels — clean look
  const PR = 8
  const PT = 24  // space for value labels above bars
  const PB = 28  // space for month labels
  const chartW = W - PL - PR
  const chartH = H - PT - PB

  const slotW = chartW / 12
  const barW = slotW * 0.55
  const barOff = (slotW - barW) / 2

  // Grid lines at 25% intervals
  const gridPcts = [0.25, 0.5, 0.75, 1]

  const fmtShort = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
    return String(n)
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ display: 'block', height: 'auto' }}
      role="img"
      aria-label="Monthly expense bar chart"
    >
      {/* Horizontal grid lines */}
      {gridPcts.map((pct) => {
        const y = PT + chartH * (1 - pct)
        return (
          <g key={pct}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#f3f4f6" strokeWidth={1} />
            <text x={W - PR + 2} y={y + 3.5} fontSize={9} fill="#d1d5db" textAnchor="start">
              {fmtShort(maxAmount * pct)}
            </text>
          </g>
        )
      })}

      {/* Baseline */}
      <line x1={PL} y1={PT + chartH} x2={W - PR} y2={PT + chartH} stroke="#e5e7eb" strokeWidth={1} />

      {/* Bars */}
      {months.map((m, i) => {
        const slotX = PL + i * slotW
        const barX = slotX + barOff
        const barH = m.total_amount > 0 ? (m.total_amount / maxAmount) * chartH : 0
        const barY = PT + chartH - barH
        const isSelected = m.month === selectedMonth
        const isHovered = m.month === hoveredMonth
        const showLabel = isSelected || isHovered

        return (
          <g
            key={m.month}
            style={{ cursor: 'pointer' }}
            onClick={() => onSelectMonth(m.month)}
            onMouseEnter={() => setHoveredMonth(m.month)}
            onMouseLeave={() => setHoveredMonth(null)}
          >
            {/* Invisible hit area */}
            <rect x={slotX} y={PT} width={slotW} height={chartH} fill="transparent" />

            {/* Bar */}
            <rect
              x={barX}
              y={barH > 0 ? barY : PT + chartH - 2}
              width={barW}
              height={barH > 0 ? barH : 2}
              rx={3}
              fill={
                isSelected
                  ? '#dc2626'
                  : isHovered
                  ? '#f87171'
                  : m.total_amount > 0
                  ? '#fecaca'
                  : '#f3f4f6'
              }
              style={{ transition: 'fill 0.15s' }}
            />

            {/* Value label above bar */}
            {showLabel && m.total_amount > 0 && (
              <>
                <rect
                  x={barX + barW / 2 - 28}
                  y={barY - 18}
                  width={56}
                  height={16}
                  rx={4}
                  fill={isSelected ? '#dc2626' : '#374151'}
                />
                <text
                  x={barX + barW / 2}
                  y={barY - 6}
                  textAnchor="middle"
                  fontSize={9}
                  fill="white"
                  fontWeight="600"
                >
                  {formatCurrency(m.total_amount).replace('Rp ', 'Rp ')}
                </text>
              </>
            )}

            {/* Month label */}
            <text
              x={slotX + slotW / 2}
              y={H - 8}
              textAnchor="middle"
              fontSize={10.5}
              fill={isSelected ? '#dc2626' : '#9ca3af'}
              fontWeight={isSelected ? '700' : '400'}
            >
              {MONTH_ABBRS[i]}
            </text>

            {/* Entry count dot under selected */}
            {isSelected && m.entry_count > 0 && (
              <circle cx={slotX + slotW / 2} cy={H - 2} r={2} fill="#dc2626" />
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const LunchExpenseListPage = () => {
  const { user } = useAuth()
  const currentDate = new Date()
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1)

  // Check if user is admin or HR
  const isAdminOrHR = user?.roles?.some(r => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  // Fetch expenses
  const { data, isLoading } = useQuery({
    queryKey: ['lunchExpenses', selectedYear, selectedMonth],
    queryFn: () => lunchExpenseApi.getExpenses({
      year: selectedYear,
      month: selectedMonth,
      limit: 31
    }),
  })

  // Fetch yearly summary
  const { data: summaryData } = useQuery({
    queryKey: ['lunchExpenseSummary', selectedYear],
    queryFn: () => lunchExpenseApi.getSummary(selectedYear),
  })

  const expenses = data?.data || []
  const summary = data?.summary
  const yearlySummary = summaryData?.data

  // Generate year options (current year and 5 years back)
  const yearOptions = useMemo(() => {
    const years = []
    for (let i = 0; i < 6; i++) {
      years.push(currentDate.getFullYear() - i)
    }
    return years
  }, [])

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('id-ID', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lunch Expenses</h1>
          <p className="text-gray-600 mt-1">Track daily lunch expenses</p>
        </div>
        {isAdminOrHR && (
          <Link
            to="/lunch-expense/admin"
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Manage Expenses
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-4 py-2 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-red-500 text-gray-900"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="px-4 py-2 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-red-500 text-gray-900"
            >
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Monthly Total */}
        <div className="bg-gradient-to-br from-red-500 to-red-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Monthly Total</p>
              <p className="text-2xl font-bold mt-1">
                {formatCurrency(summary?.total_amount || 0)}
              </p>
              <p className="text-red-100 text-sm mt-1">
                {summary?.total_entries || 0} entries
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Yearly Total */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Yearly Total ({selectedYear})</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(yearlySummary?.yearly_total || 0)}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {yearlySummary?.yearly_entry_count || 0} entries
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Daily Average */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Monthly Average</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(
                  summary?.total_entries
                    ? (summary.total_amount / summary.total_entries)
                    : 0
                )}
              </p>
              <p className="text-gray-500 text-sm mt-1">per day</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expense List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </h2>
        </div>

        {expenses.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No expenses recorded</h3>
            <p className="text-gray-500">No lunch expenses have been recorded for this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {formatDate(expense.expense_date)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(expense.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500">
                        {expense.notes || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900">
                    {formatCurrency(summary?.total_amount || 0)}
                  </td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Monthly Bar Chart */}
      {yearlySummary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Monthly Breakdown
            </h2>
            <span className="text-sm text-gray-400">{selectedYear}</span>
          </div>
          <p className="text-xs text-gray-400 mb-4">Click a bar to filter the table below</p>
          <MonthlyBarChart
            breakdown={yearlySummary.monthly_breakdown}
            selectedMonth={selectedMonth}
            onSelectMonth={setSelectedMonth}
            formatCurrency={formatCurrency}
          />
          {/* Selected month callout */}
          {(() => {
            const m = yearlySummary.monthly_breakdown.find((b) => b.month === selectedMonth)
            return m && m.total_amount > 0 ? (
              <div className="mt-3 flex items-center gap-4 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                <span className="text-sm font-semibold text-red-700">{m.month_name}</span>
                <span className="text-sm text-red-600">{formatCurrency(m.total_amount)}</span>
                <span className="text-xs text-red-400">{m.entry_count} {m.entry_count === 1 ? 'day' : 'days'}</span>
              </div>
            ) : null
          })()}
        </div>
      )}
    </div>
  )
}

export default LunchExpenseListPage
